import { useSnackbar } from "notistack";
import {
  backendDelete,
  backendGet,
  backendPost,
} from "@accredo/fe-shared/services/dataService.js";

/**
 * Mutations on the portal-owned side of a bank account: the review decision,
 * the customer's default account, and object links.
 *
 * Imported accounts are not usable for claim payments until someone confirms
 * them here — nothing OpenViva delivers is reliable enough to decide that
 * automatically.
 */
export function useBankAccountActions() {
  const { enqueueSnackbar } = useSnackbar();

  /**
   * kunde_id is omitted for a customer-scoped user — the backend resolves
   * their own customer rather than making them pick the one they're allowed
   * to choose. Accredo/sysadmin must supply it.
   */
  const createAccount = async (payload) =>
    await backendPost("data/bank-accounts", {
      label: "Bankkonto",
      toSend: payload,
      enqueueSnackbar,
      successMessage: "Bankkonto wurde angelegt.",
    });

  const setReviewState = async (bank_account_id, review_state) =>
    await backendPost(`data/bank-accounts/${bank_account_id}/review`, {
      label: "Prüfstatus",
      toSend: { review_state },
      enqueueSnackbar,
      successMessage:
        review_state === "claims_relevant"
          ? "Konto für Schadenzahlungen freigegeben."
          : "Konto als nicht relevant markiert.",
    });

  const setCustomerDefault = async (bank_account_id, is_default) =>
    await backendPost(`data/bank-accounts/${bank_account_id}/default`, {
      label: "Standardkonto",
      toSend: { is_default },
      enqueueSnackbar,
      successMessage: is_default
        ? "Konto ist jetzt das Standardkonto des Kunden."
        : "Standardkonto entfernt.",
    });

  const detachObject = async (bank_account_id, objekt_id) =>
    await backendDelete(
      `data/bank-accounts/${bank_account_id}/objects/${objekt_id}`,
      {
        label: "Objektverknüpfung",
        enqueueSnackbar,
        successMessage: "Verknüpfung entfernt.",
      }
    );

  /**
   * The customer's objects for the bulk assignment mask, each carrying the
   * account it currently uses so replacements can be shown before committing.
   */
  const fetchAssignableObjects = async (bank_account_id, params, setResult) =>
    await backendGet(
      `data/bank-accounts/${bank_account_id}/assignable-objects`,
      {
        label: "Objekte",
        enqueueSnackbar,
        setResult,
        options: { params },
      }
    );

  /**
   * Link many objects at once. With dry_run the backend writes nothing and
   * reports what would happen — used to confirm replacements first, since
   * linking silently replaces an object's existing account.
   */
  const batchAssignObjects = async (bank_account_id, objekt_ids, dry_run) =>
    await backendPost(`data/bank-accounts/${bank_account_id}/objects/batch`, {
      label: "Objektzuordnung",
      toSend: { objekt_ids, dry_run },
      enqueueSnackbar,
      ...(!dry_run && { successMessage: "Objekte wurden zugeordnet." }),
    });

  /**
   * A customer's own bank accounts, unpaginated — used by the "set default"
   * confirmation to look up the account currently marked default, which may
   * not be on the page the table happens to be showing.
   */
  const fetchCustomerBankAccounts = async (kunde_id, setResult) =>
    await backendGet(`data/kundes/${kunde_id}/bank-accounts`, {
      label: "Bankkonten",
      enqueueSnackbar,
      setResult,
      options: { params: { api_version: "v2", limit: 1000 } },
    });

  /** Per-state totals for the triage tab badges (own endpoint, like claims). */
  const loadReviewStateCounts = async (setResult) =>
    await backendGet("data/bank-accounts/review-state-counts", {
      label: "Bankkonten",
      enqueueSnackbar,
      setResult: (res) => setResult(res?.data ?? null),
      options: { params: { api_version: "v2" } },
    });

  return {
    createAccount,
    setReviewState,
    setCustomerDefault,
    detachObject,
    fetchAssignableObjects,
    batchAssignObjects,
    fetchCustomerBankAccounts,
    loadReviewStateCounts,
  };
}
